/**
 * ## The password rule, as this app enforces it
 *
 * A copy of `@spontar/session-engine`'s `passwordPolicy` — copied rather than
 * imported because the back office is a separate Vite app that does not compile
 * the shared packages, and adding that build seam for forty lines would be a
 * larger change than the rule itself.
 *
 * **A test asserts the two are identical**, character for character, so this
 * cannot drift from the definition the identity platform and the app both use.
 * If it ever does, the failure is a teacher typing something this screen
 * accepted and Firebase refusing it, with no way to know which rule they broke.
 */

export const PASSWORD_POLICY = {
  minLength: 8,
  maxLength: 4096,
  requireUppercase: true,
  requireLowercase: true,
  requireNumeric: true,
  requireNonAlphanumeric: false,
} as const;

export type PasswordProblem = "tooShort" | "needsUppercase" | "needsLowercase" | "needsNumber";

export const PASSWORD_PROBLEM_KEY: Record<PasswordProblem, string> = {
  tooShort: "password.tooShort",
  needsUppercase: "password.needsUppercase",
  needsLowercase: "password.needsLowercase",
  needsNumber: "password.needsNumber",
};

/** Everything wrong with it, so nobody is sent away twice. */
export function passwordProblems(password: string): PasswordProblem[] {
  const problems: PasswordProblem[] = [];

  if (password.length < PASSWORD_POLICY.minLength) problems.push("tooShort");
  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(password)) problems.push("needsUppercase");
  if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(password)) problems.push("needsLowercase");
  if (PASSWORD_POLICY.requireNumeric && !/[0-9]/.test(password)) problems.push("needsNumber");

  return problems;
}

export function passwordIsAcceptable(password: string): boolean {
  return passwordProblems(password).length === 0;
}
